import React, { useEffect, useState, useRef } from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronRight } from "react-feather";
import { useTranslation } from "react-i18next";
import { useLiffProfile } from "../../context/LiffProfileContext";

import SectionHeaderBlogs from "../header/SectionHeaderBlogs";
import SectionHeaderPromo from "../header/SectionHeaderPromo";

// helper แปลงวันที่ให้ตรงภาพ (ภาษาไทยเพิ่ม พ.ศ. +543)
// helper: แสดงวันที่แบบการ์ดข่าว
// th  -> "5 กันยายน 2568"
// en  -> "5 September 2025"

const formatBlogDate = (i18n, iso) => {
  if (!iso) return "";
  const isTH = i18n?.language?.startsWith("th");
  const d = new Date(iso);

  try {
    // ใช้ Buddhist calendar + เดือนไทย และล็อกโซนเวลาไทย
    const df = new Intl.DateTimeFormat(isTH ? "th-TH-u-ca-buddhist" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Bangkok",
    });
    return df.format(d);
  } catch {
    // Fallback: moment (เพิ่ม พ.ศ. เอง)
    const m = moment.tz(iso, "Asia/Bangkok").locale(isTH ? "th" : "en");
    const y = m.year() + (isTH ? 543 : 0);
    return m.format("D MMMM ") + y;
  }
};

const ShowOthersSection = ({ sections, onLoaded }) => {
  const { t, i18n } = useTranslation();
  const { authToken, userId } = useLiffProfile();
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  const blogCarouselRef = useRef(null);
  const promotionCarouselRef = useRef(null);
  const [promotions, setPromotions] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    sessionStorage.setItem("previousPath", window.location.pathname);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Promotions (จาก sections)
        const eventSection = sections.find(
          (section) => section.type?.toLowerCase() === "event"
        );

        if (eventSection && Array.isArray(eventSection.items)) {
          const eventData = eventSection.items.map((item) => ({
            image: item.image,
            name: item.name,
            detail: item.periodDescs?.[t("language")] || item.periodDescs?.en,
            targetURI: item.targetURI,
          }));
          setPromotions(eventData);
        }
      } catch (err) {
        console.error("Error fetching promotions:", err.message);
        setError(err.message);
      }
    };

    fetchData();
  }, [sections, t]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BIGC_BIGLINE_API_URL}/blogs/blogs`,
          {
            status: "publish",
            orderby: "date",
            order: "desc",
            per_page: "6",
            page: "1",
            language: i18n.language.toUpperCase(),
          },
          {
            headers: {
              "Content-Type": "application/json",
              "x-api-key": process.env.REACT_APP_BIGC_BIGLINE_API_KEY,
            },
          }
        );

        const blogData = response.data.map((item) => ({
          postId: item.id,
          image: item.image,
          name: item.title.rendered,
          date: item.date,
          targetURI: `${item.link
            .replace("https://blog-corporate.bigc.co.th/", "/blogs/")
            .replace(/\/$/, "")}?id=${item.id}`,
        }));

        setBlogs(blogData);
      } catch (err) {
        console.error("Error loading blogs:", err.message);
        setError(err.message);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    if (promotions.length > 0 || blogs.length > 0) {
      onLoaded && onLoaded();
    }
  }, [promotions, blogs, onLoaded]);

  return (
    <div>
      {/* Promotion Section */}
      {promotions.length > 0 ? (
        <div className="w-full bg-primary-section pt-3 pb-4">
          <div className="w-full h-14 flex justify-between items-center px-5 xs:px-2">
            <div className="text-xl font-semibold">
              <SectionHeaderPromo
                className="py-4"
                name={t("main_promotion")}
                eventSection={true}
                seniorLayout={false}
              />
            </div>
            <Link
              to="/promotion"
              className="flex items-center cursor-pointer text-danger text-sm mr-2"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: "home_promotion_click",
                  promotion_target: "/promotion",
                  promotion_name: "see_more",
                  promotion_index: null,
                });
              }}
            >
              {t("see_more")}
              {/*<ChevronRight className="w-4 h-full ml-1" />*/}
            </Link>
          </div>
          <div className="relative">
            <div className="min-h-[220px] h-[223px]">
              <div
                ref={promotionCarouselRef}
                className="w-full flex flex-row flex-nowrap overflow-x-auto gap-x-4 pl-5 xs:pl-2 no-scrollbars  snap-x snap-mandatory"
              >
                {promotions.slice(0, 5).map((item, index) => (
                  <div
                    key={`promotion-${index}`}
                    className="w-40 flex-shrink-0"
                  >
                    <a
                      href={item.targetURI}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full no-underline"
                      onClick={() => {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          event: "home_promotion_click",
                          promotion_target: item.targetURI,
                          promotion_name: item.name,
                          promotion_index: index,
                        });
                      }}
                    >
                      <div className="w-40 rounded-2xl border border-overlay-12 overflow-hidden">
                        <div className="relative aspect-[5/7] bg-gray-100">
                          <img
                            src={item.image}
                            alt={item.name}
                            //className="object-cover object-top w-full h-full"
                            className="absolute inset-0 w-full h-full object-cover object-top"
                            loading="lazy"
                            decoding="async"
                            width={800}
                            height={1000}
                          />
                        </div>
                      </div>
                    </a>
                    {/*<div
                    className={`px-2 font-normal pt-1.5 line-clamp-2 text-base text-left ${
                      seniorLayout ? "text-lg" : "text-base"
                    }`}
                  >
                    <Link
                      to={item.targetURI}
                      className="text-primary"
                      onClick={() => {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          event: "home_promotion_click",
                          promotion_target: item.targetURI,
                          promotion_name: item.name,
                          promotion_index: index,
                        });
                      }}
                    >
                      {item.name}
                    </Link>
                  </div>
                  <div className="px-2 font-normal text-danger text-xs h-4 text-left">
                    {item.detail}
                  </div>*/}
                  </div>
                ))}

                {/* item 6: ปุ่มดูเพิ่มเติม */}
                <div className="w-40 flex-shrink-0 flex items-center justify-center border border-dashed border-gray-300 rounded-xl text-center">
                  <Link
                    to="/promotion"
                    className="text-primary font-medium text-sm px-4 py-2"
                    onClick={() => {
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "home_promotion_click",
                        promotion_target: "/promotion",
                        promotion_name: "see_more",
                        promotion_index: null,
                      });
                    }}
                  >
                    {t("see_more")} →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}

      {/* Blogs Section */}
      {blogs.length > 0 ? (
        <div className="w-full bg-gray-100 py-6 mb-2">
          <div className="w-full h-14 flex justify-between items-center px-5 xs:px-2">
            <div className="text-xl font-semibold">
              <SectionHeaderBlogs
                className="py-4"
                name={t("blogs")}
                eventSection={true}
                seniorLayout={false}
              />
            </div>
            <Link
              to="/blogs"
              className="flex items-center cursor-pointer text-danger text-sm mr-2"
              onClick={() => {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: "home_blog_click",
                  promotion_target: "/blogs",
                  promotion_name: "see_more",
                  promotion_index: null,
                });
              }}
            >
              {t("see_more")}
              {/*<ChevronRight className="w-4 h-full ml-1" />*/}
            </Link>
          </div>
          <div className="relative">
            <div
              ref={blogCarouselRef}
              className="w-full flex flex-row flex-nowrap overflow-x-auto gap-x-6 pl-5 xs:pl-2 no-scrollbars h-[310px]"
            >
              {blogs.slice(0, 5).map((item, index) => {
                const dateStr = formatBlogDate(
                  i18n,
                  item.date ||
                    item.publishedAt ||
                    item.updatedAt ||
                    item.createdAt ||
                    item.updated_date ||
                    item.created_date
                );

                return (
                  <div
                    key={`blog-${index}`}
                    className="w-[300px] sm:w-[340px] shrink-0 snap-start"
                  >
                    <Link
                      to={item.targetURI}
                      className="block group"
                      onClick={() => {
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                          event: "home_blog_click",
                          blog_target: item.targetURI,
                          blog_name: item.name,
                          blog_index: index,
                        });
                      }}
                    >
                      <article className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition overflow-hidden">
                        {/* รูปด้านบน 16:9 */}
                        <div className="relative aspect-[16/9] bg-gray-100">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            width={1280}
                            height={720}
                          />
                        </div>

                        {/* เนื้อหา */}
                        <div className="px-4 pt-3 pb-4 text-left">
                          <h3 className="text-gray-900 font-semibold leading-6 line-clamp-2 text-base sm:text-lg">
                            {item.name}
                          </h3>

                          {/* เส้นคั่น + วันที่ */}
                          {dateStr && (
                            <div className="mt-3 pt-2 border-t border-gray-200 text-gray-500 text-sm">
                              {dateStr}
                            </div>
                          )}
                        </div>
                      </article>
                    </Link>
                  </div>
                );
              })}

              {/* ปุ่มดูเพิ่มเติม */}
              <div className="w-[300px] sm:w-[340px] shrink-0 snap-start flex items-stretch">
                <Link
                  to="/blogs"
                  className="w-full"
                  onClick={() => {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "home_blog_click",
                      promotion_target: "/blogs",
                      promotion_name: "see_more",
                      promotion_index: null,
                    });
                  }}
                >
                  <article className="h-full bg-white rounded-2xl border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm transition flex items-center justify-center">
                    <span className="text-primary font-medium text-base">
                      {t("see_more")} →
                    </span>
                  </article>
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default ShowOthersSection;
